import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addDays,
  addWeeks,
  addMonths,
  format,
  startOfWeek,
  endOfWeek,
} from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { usePermissions } from '../../company-management/hooks/usePermissions';
import { PERMISSIONS } from '../../company-management/constants/permissions';
import { useTeamLabelCalendarData } from '../hooks/useTeamLabelCalendarData';
import TeamLabelCalendarGrid from '../components/TeamLabelCalendarGrid';
import TeamLabelCalendarWeekView from '../components/TeamLabelCalendarWeekView';
import TeamLabelCalendarMonthView from '../components/TeamLabelCalendarMonthView';
import TeamLabelStaffFilter from '../components/TeamLabelStaffFilter';
import TeamLabelLocationFilter from '../components/TeamLabelLocationFilter';
import TeamLabelCalendarSyncDialog from '../components/sync/TeamLabelCalendarSyncDialog';
import { CalendarDragDropProvider } from '../drag-drop/CalendarDragDropContext';
import { useTeamCalMobileLayout } from '../hooks/useTeamCalMobileLayout';
import { MEETINGS_COLUMN_ID } from '../utils/columnMatch';
import { CalendarGridSkeleton } from '../../components/loading/skeletons';
import '../styles/TeamLabelCalendar.css';

const ChevronLeftIcon = () => (
  <svg className="team-cal-v3__chevron" viewBox="0 0 24 24" aria-hidden>
    <path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="team-cal-v3__chevron" viewBox="0 0 24 24" aria-hidden>
    <path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
  </svg>
);

const SyncIcon = () => (
  <svg className="team-cal-v3__chevron" viewBox="0 0 24 24" aria-hidden>
    <path
      fill="currentColor"
      d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"
    />
  </svg>
);

export default function TeamLabelCalendarPage() {
  const { user, companyData, loading: authLoading } = useAuth();
  const { hasPermission } = usePermissions();
  const { setNotification } = useNotification();
  const [syncOpen, setSyncOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [view, setView] = useState('day');
  const [itemTypeFilter, setItemTypeFilter] = useState('all');
  const [locationFilterId, setLocationFilterId] = useState(null);
  const [staffFilterIds, setStaffFilterIds] = useState(null);
  const isMobileLayout = useTeamCalMobileLayout();

  const { loading, error, companyHours, appointmentsInRange, appointmentsForSelectedDay, columns, intervalBounds } =
    useTeamLabelCalendarData({
      companyId: companyData?.id,
      ownerId: companyData?.ownerId,
      user,
      selectedDate,
      view,
      itemTypeFilter,
      locationFilterId,
    });

  const columnIdsKey = useMemo(
    () => columns.map((c) => c.id).sort().join('|'),
    [columns]
  );

  const columnIdSet = useMemo(() => new Set(columns.map((c) => c.id)), [columnIdsKey]);

  useEffect(() => {
    setStaffFilterIds(null);
  }, [itemTypeFilter]);

  useEffect(() => {
    setStaffFilterIds((current) => {
      if (current == null) return current;
      const next = new Set([...current].filter((id) => columnIdSet.has(id)));
      if (next.size === 0) return null;
      if (next.size === current.size && [...next].every((id) => current.has(id))) {
        return current;
      }
      return next;
    });
  }, [columnIdSet]);

  const displayColumns = useMemo(() => {
    const meetingsCol = columns.find((c) => c.id === MEETINGS_COLUMN_ID);
    const staffLike = columns.filter((c) => c.id !== MEETINGS_COLUMN_ID);

    const staffPart =
      staffFilterIds == null ? staffLike : staffLike.filter((c) => staffFilterIds.has(c.id));

    if (itemTypeFilter === 'meetings') {
      return meetingsCol ? [meetingsCol] : [];
    }

    if (itemTypeFilter === 'all' && meetingsCol) {
      return [...staffPart, meetingsCol];
    }

    return staffPart;
  }, [columns, staffFilterIds, itemTypeFilter]);

  const showStaffFilter =
    user?.type !== 'employee' && columns.filter((c) => c.id !== MEETINGS_COLUMN_ID).length > 1;

  const showSyncControl = Boolean(companyData?.id);
  const canDragGrid =
    !isMobileLayout &&
    hasPermission(PERMISSIONS.CALENDAR.EDIT) &&
    (view === 'day' || view === 'week');
  const canDragMonth =
    !isMobileLayout && hasPermission(PERMISSIONS.CALENDAR.EDIT) && view === 'month';

  const handleDropResult = useCallback(
    (result) => {
      if (result.noop) return;

      if (result.ok) {
        setNotification({
          open: true,
          message: `Appointment rescheduled to ${format(result.newStartTime, 'EEEE, MMM d • h:mm a')}.`,
          severity: 'success',
          autoHideDuration: 3500,
        });
        return;
      }

      if (result.reason) {
        setNotification({
          open: true,
          message: result.reason,
          severity: 'error',
          autoHideDuration: 4500,
        });
      }
    },
    [setNotification]
  );

  const rangeTitle = useMemo(() => {
    if (!selectedDate) return '';
    if (view === 'day') return format(selectedDate, 'EEEE, MMM d, yyyy');
    if (view === 'week') {
      const w0 = startOfWeek(selectedDate, { weekStartsOn: 0 });
      const w1 = endOfWeek(selectedDate, { weekStartsOn: 0 });
      return `${format(w0, 'MMM d')} – ${format(w1, 'MMM d, yyyy')}`;
    }
    return format(selectedDate, 'MMMM yyyy');
  }, [view, selectedDate]);

  const goPrev = () => {
    setSelectedDate((d) => {
      if (view === 'day') return addDays(d, -1);
      if (view === 'week') return addWeeks(d, -1);
      return addMonths(d, -1);
    });
  };

  const goNext = () => {
    setSelectedDate((d) => {
      if (view === 'day') return addDays(d, 1);
      if (view === 'week') return addWeeks(d, 1);
      return addMonths(d, 1);
    });
  };

  const setViewMode = (id) => {
    if (id === 'day') {
      setSelectedDate(new Date());
      setView('day');
    } else {
      setView(id);
    }
  };

  const navLabel =
    view === 'day' ? 'day' : view === 'week' ? 'week' : 'month';

  if (authLoading || !companyData) {
    return (
      <div className="team-cal-v3-page">
        <CalendarGridSkeleton columns={view === 'week' ? 7 : 5} label="Loading calendar" />
      </div>
    );
  }

  return (
    <div
      className={`team-cal-v3-page${
        view === 'month' ? ' team-cal-v3-page--month' : view === 'week' ? ' team-cal-v3-page--week' : ''
      }`}
    >
      <div className="team-cal-v3-shell" aria-label="Team calendar">
        <header className="team-cal-v3-toolbar">
          <div className="team-cal-v3-toolbar__left">
            <div className="team-cal-v3-nav-arrows" role="group" aria-label="Date navigation">
              <button type="button" className="team-cal-v3-icon-btn" onClick={goPrev} aria-label={`Previous ${navLabel}`}>
                <ChevronLeftIcon />
              </button>
              <button type="button" className="team-cal-v3-icon-btn" onClick={goNext} aria-label={`Next ${navLabel}`}>
                <ChevronRightIcon />
              </button>
            </div>
            <div className="team-cal-v3-item-type" role="tablist" aria-label="Appointments and meetings">
              {[
                { id: 'all', label: 'All' },
                { id: 'appointments', label: 'Appointments' },
                { id: 'meetings', label: 'Meetings' },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={itemTypeFilter === id}
                  className={`team-cal-v3-item-type__btn${itemTypeFilter === id ? ' team-cal-v3-item-type__btn--active' : ''}`}
                  onClick={() => setItemTypeFilter(id)}
                >
                  {label}
                </button>
              ))}
            </div>
            {showStaffFilter ? (
              <TeamLabelStaffFilter
                columns={columns.filter((c) => c.id !== MEETINGS_COLUMN_ID)}
                selectedIds={staffFilterIds}
                onChange={setStaffFilterIds}
              />
            ) : null}
            <TeamLabelLocationFilter
              companyId={companyData?.id}
              companyData={companyData}
              value={locationFilterId}
              onChange={setLocationFilterId}
            />
          </div>
          <h1 className="team-cal-v3-toolbar__title">{rangeTitle}</h1>
          <div className="team-cal-v3-toolbar__right">
            {showSyncControl ? (
              <button
                type="button"
                className="team-cal-v3-sync-btn"
                onClick={() => setSyncOpen(true)}
                title="Google or Outlook calendar sync"
                aria-label="Calendar sync (Google or Outlook)"
              >
                <SyncIcon />
                <span className="team-cal-v3-sync-btn__label">Sync</span>
              </button>
            ) : null}
            <div className="team-cal-v3-view-switch" role="group" aria-label="Calendar view">
              {['day', 'week', 'month'].map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`team-cal-v3-view-switch__btn${view === id ? ' team-cal-v3-view-switch__btn--active' : ''}`}
                  onClick={() => setViewMode(id)}
                  aria-pressed={view === id}
                >
                  {id === 'day' ? 'Today' : id === 'week' ? 'Week' : 'Month'}
                </button>
              ))}
            </div>
          </div>
        </header>

        <CalendarDragDropProvider
          companyId={companyData?.id}
          companyData={companyData}
          companyHours={companyHours}
          user={user}
          canDragGrid={canDragGrid}
          canDragMonth={canDragMonth}
          onDropResult={handleDropResult}
        >
        <div
          className={[
            'team-cal-v3-shell__body',
            view === 'day' ? 'team-cal-v3-shell__body--day' : '',
            view === 'week' ? 'team-cal-v3-shell__body--week' : '',
            view === 'month' ? 'team-cal-v3-shell__body--month' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {error ? <p className="team-cal-v3-error">{error}</p> : null}

          {loading ? (
            <div className="team-cal-v3-loading-host">
              <CalendarGridSkeleton columns={view === 'week' ? 7 : 5} showToolbar={false} label="Loading calendar" />
            </div>
          ) : displayColumns.length === 0 ? (
            <p className="team-cal-v3-muted team-cal-v3-shell__empty">No team members found for this company.</p>
          ) : view === 'day' ? (
            <div className="team-cal-v3-day-mount">
              <TeamLabelCalendarGrid
                selectedDate={selectedDate}
                companyHours={companyHours}
                columns={displayColumns}
                appointmentsForDay={appointmentsForSelectedDay}
              />
            </div>
          ) : view === 'week' && intervalBounds ? (
            <TeamLabelCalendarWeekView
              weekStart={intervalBounds.start}
              weekEnd={intervalBounds.end}
              companyHours={companyHours}
              columns={displayColumns}
              appointmentsInRange={appointmentsInRange}
            />
          ) : (
            <TeamLabelCalendarMonthView
              monthAnchor={selectedDate}
              appointmentsInRange={appointmentsInRange}
              onSelectDay={(day) => {
                setSelectedDate(day);
                setView('day');
              }}
            />
          )}
        </div>
        </CalendarDragDropProvider>
      </div>

      <TeamLabelCalendarSyncDialog
        open={syncOpen}
        onClose={() => setSyncOpen(false)}
      />
    </div>
  );
}
